import { useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

interface ChatInputProps {
  onSend: (content: string) => void
  /** Called when the user clicks the Stop button during AI generation. */
  onStop?: () => void
  /** Fully disables the input bar (session paused / not active). */
  disabled?: boolean
  /** Whether the AI is currently streaming a response. */
  isStreaming?: boolean
  placeholder?: string
}

export function ChatInput({
  onSend,
  onStop,
  disabled = false,
  isStreaming = false,
  placeholder,
}: Readonly<ChatInputProps>) {
  const { t } = useTranslation()
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  function autoResize() {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    const computed = getComputedStyle(el)
    const lineHeight = Number.parseFloat(computed.lineHeight) || 20
    const maxHeight = lineHeight * 4 + Number.parseFloat(computed.paddingTop) + Number.parseFloat(computed.paddingBottom)
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`
  }

  const handleSubmit = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    const content = el.value.trim()
    // Block send if the session is disabled or AI is still streaming
    if (!content || disabled || isStreaming) return
    onSend(content)
    el.value = ''
    autoResize()
  }, [onSend, disabled, isStreaming])

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSubmit()
    }
  }

  // Restore focus when streaming ends so the user can type immediately
  useEffect(() => {
    if (!disabled && !isStreaming) {
      textareaRef.current?.focus()
    }
  }, [disabled, isStreaming])

  const showStopButton = isStreaming && !!onStop

  return (
    <div className="px-3 py-3 shrink-0 bg-surface/80 backdrop-blur-xl border-t border-outline-variant/30">
      <div className="flex items-end gap-2 bg-white rounded-2xl border border-outline-variant/50 shadow-[0px_4px_12px_rgba(0,0,0,0.06)] px-3 py-2">
        <textarea
          ref={textareaRef}
          rows={1}
          disabled={disabled}
          placeholder={placeholder ?? t('session.placeholder')}
          onChange={autoResize}
          onKeyDown={handleKeyDown}
          className="flex-1 resize-none bg-transparent text-sm text-on-surface placeholder-on-surface-variant/50 focus:outline-none disabled:text-on-surface-variant/40 min-h-[36px] leading-relaxed py-1"
        />

        {showStopButton ? (
          <button
            type="button"
            onClick={onStop}
            aria-label={t('session.stop')}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-error/10 text-[#B3261E] hover:bg-error/20 transition-colors shrink-0"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
              <path d="M6 6h12v12H6z" />
            </svg>
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={disabled || isStreaming}
            aria-label={t('session.send')}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-primary text-white hover:opacity-90 disabled:opacity-40 transition-all shrink-0"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        )}
      </div>
      <p className="text-[10px] text-on-surface-variant/50 text-center mt-1">{t('session.send_hint')}</p>
    </div>
  )
}
